import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { CustomSelect, FilterOptions, HomeworkStatusOptions, HomeworkTypeOptions, TermFilter } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/homeworks/HomeworksAction";
import { useHomeworksUIContext } from "../HomeworksUIContext";

const prepareFilter = (queryParams, values) => {
  let { term, teacherId, sectionId, studentId, courseId, homeworkType, createDate, createDateEnd, definition, attachmentName, isEvaluated } = values;
  const newQueryParams = { ...queryParams };
  const filter = { teacherId, sectionId, studentId, courseId, homeworkType, createDate, createDateEnd, definition, attachmentName, isEvaluated };
  newQueryParams.filter = filter;
  newQueryParams.term = term;
  return newQueryParams;
};

export function HomeworksFilter() {
  // Homeworks UI Context
  const homeworksUIContext = useHomeworksUIContext();
  const homeworksUIProps = useMemo(() => {
    return {
      queryParams: homeworksUIContext.queryParams,
      setQueryParams: homeworksUIContext.setQueryParams
    };
  }, [homeworksUIContext]);

  const dispatch = useDispatch();
  const { currentActiveTerm, teachers, sections, students, courses } = useSelector(
    (state) => ({
      currentActiveTerm: state.auth.user.currentActiveTerm,
      teachers: state.homeworks.teachers,
      sections: state.homeworks.sections,
      students: state.homeworks.students,
      courses: state.homeworks.courses
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(actions.getAllList());
  }, [dispatch]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(homeworksUIProps.queryParams, values);
    if (!isEqual(newQueryParams, homeworksUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      homeworksUIProps.setQueryParams(newQueryParams);
    }
  };

  const teacherOptions = FilterOptions.concat(teachers);
  const sectionOptions = FilterOptions.concat(sections);
  const studentOptions = FilterOptions.concat(students);
  const courseOptions = FilterOptions.concat(courses);
  const homeworkTypeOptions = FilterOptions.concat(HomeworkTypeOptions);

  return (
    <Formik
      initialValues={{
        term: currentActiveTerm,
        createDate: "",
        createDateEnd: "",
        definition: "",
        attachmentName: ""
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right">
          <TermFilter
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            handleChange={handleChange}
            onChange={e => {
              if (values.sectionId) dispatch(actions.getStudents(e.target.value, values.sectionId));
              dispatch(actions.getList(e.target.value));
            }}
            values={values}
          />
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Tarih
            </label>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="createDate"
                placeholder="Başlangıç"
                onBlur={handleBlur}
                value={values.createDate}
                onChange={(e) => {
                  setFieldValue("createDate", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="createDateEnd"
                placeholder="Bitiş"
                onBlur={handleBlur}
                value={values.createDateEnd}
                onChange={(e) => {
                  setFieldValue("createDateEnd", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Sınıf
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={sectionOptions}
                value={values.sectionId}
                placeholder="Sınıf"
                onBlur={handleBlur}
                onChange={e => {
                  if (e.value) dispatch(actions.getStudents(values.term, e.value));
                  dispatch(actions.cleanStudentsState());
                  setFieldValue("sectionId", e.value);
                  setFieldValue("studentId", undefined);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Öğrenci
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={studentOptions}
                value={values.studentId}
                placeholder="Öğrenci"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("studentId", e.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg-2">
              <CustomSelect
                options={teacherOptions}
                value={values.teacherId}
                placeholder="Öğretmen"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("teacherId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Öğretmen</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect
                options={courseOptions}
                value={values.courseId}
                placeholder="Ders"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("courseId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ders</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={homeworkTypeOptions}
                value={values.homeworkType}
                placeholder="Ödev Türü"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("homeworkType", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ödev Türü</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="definition"
                placeholder="Ödev Tanımı"
                onBlur={handleBlur}
                value={values.definition}
                onChange={(e) => {
                  setFieldValue("definition", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ödev Tanımı</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="attachmentName"
                placeholder="Ek"
                onBlur={handleBlur}
                value={values.attachmentName}
                onChange={(e) => {
                  setFieldValue("attachmentName", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ek</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect
                options={HomeworkStatusOptions}
                value={values.isEvaluated}
                placeholder="Durum"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("isEvaluated", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Durum</b>
              </small>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}